import { Box } from '@osmosis/react-ui/components/Box';
import { Checkbox } from '@osmosis/react-ui/components/Checkbox';
import { H4 } from '@osmosis/react-ui/components/Text';
import React from 'react';
import { useIntl } from 'react-intl';
import { QuizItem } from '../../types/quiz';
import useQuizProgress from './useQuizProgress';

type QuestionFilterPanelProps = {
  quizItems: QuizItem[];
  quizType: string;
  filters: {
    remaining: boolean;
    correctHigh: boolean;
    correctMedium: boolean;
    correctLow: boolean;
    incorrectHigh: boolean;
    incorrectMedium: boolean;
    incorrectLow: boolean;
  };
  onFilterChange: (
    filterKey:
      | 'remaining'
      | 'correctHigh'
      | 'correctMedium'
      | 'correctLow'
      | 'incorrectHigh'
      | 'incorrectMedium'
      | 'incorrectLow',
  ) => void;
  isQuizBuilder?: boolean;
  isFlashCard?: boolean;
};

/**
 * Renders Quiz question filter panel.
 */
const QuizQuestionFilterPanel: React.FC<QuestionFilterPanelProps> = ({
  quizItems,
  quizType,
  filters,
  onFilterChange,
  isQuizBuilder,
  isFlashCard,
}) => {
  const intl = useIntl();
  const {
    incorrectHigh,
    numberOfNewItems,
    incorrectLow,
    incorrectMedium,
    correctHigh,
    correctMedium,
    correctLow,
  } = useQuizProgress(
    quizItems?.map((item) => ({
      item,
      answered: !!item.history,
      accuracy: item.history?.lastAccuracy,
      confidence: item.history?.lastConfidence,
      time: item.history?.timestamp,
    })),
  );
  const quizTypeLabel = isFlashCard
    ? intl.formatMessage({
        id: 'quiz.question_filter_panel.flashcards',
        defaultMessage: 'flashcards',
      })
    : intl.formatMessage({
        id: 'quiz.question_filter_panel.questions',
        defaultMessage: 'questions',
      });
  const quizTypeCapitalized =
    quizTypeLabel.charAt(0).toUpperCase() + quizTypeLabel.slice(1);

  return (
    <Box
      sx={{
        maxWidth: 700,
        margin: '0 auto',
        fontFamily: 'inherit',
        padding: 4,
      }}
      data-testid="quiz-question-filter-panel"
    >
      <H4
        style={{ fontWeight: 600, color: '#404040' }}
        data-testid="quiz-question-filter-panel-title"
      >
        {intl.formatMessage({
          id: 'quiz.question_filter_panel.title',
          defaultMessage: isQuizBuilder ? 'Filter' : 'Include',
        })}
      </H4>
      <Box sx={{ marginY: 4 }} data-testid="quiz-question-filter-remaining">
        <Checkbox
          data-testid={`filter-remaining`}
          label={
            isQuizBuilder
              ? intl.formatMessage(
                  {
                    id: 'quiz.question_filter_panel.new_questions',
                    defaultMessage:
                      'New {quizTypeLabel} only ({numberOfNewItems})',
                  },
                  { numberOfNewItems, quizTypeLabel },
                )
              : intl.formatMessage(
                  {
                    id: 'quiz.question_filter_panel.remaining',
                    defaultMessage:
                      'Remaining {quizType} only ({numberOfNewItems})',
                  },
                  { quizType, numberOfNewItems },
                )
          }
          checked={filters.remaining}
          onChange={() => onFilterChange('remaining')}
        />
      </Box>
      <Box
        style={{
          display: 'flex',
          gap: 40,
          alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid="quiz-question-filter-checkbox-groups"
      >
        {/* Correct Questions */}
        <fieldset
          style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
          data-testid="quiz-question-filter-correct-group"
        >
          <legend
            style={{ color: '#406040', fontSize: 16, marginBottom: 16 }}
            data-testid="quiz-question-filter-correct-title"
          >
            {intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.correct',
                defaultMessage: `Correct {quizTypeCapitalized}`,
              },
              { quizTypeCapitalized },
            )}
          </legend>
          <Checkbox
            data-testid="filter-correctHigh"
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.correct_high',
                defaultMessage: 'High confidence ({count})',
              },
              { count: correctHigh.length },
            )}
            checked={filters.correctHigh}
            onChange={() => onFilterChange('correctHigh')}
          />
          <Checkbox
            data-testid="filter-correctMedium"
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.correct_medium',
                defaultMessage: 'Medium confidence ({count})',
              },
              { count: correctMedium.length },
            )}
            checked={filters.correctMedium}
            onChange={() => onFilterChange('correctMedium')}
          />
          <Checkbox
            data-testid="filter-correctLow"
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.correct_low',
                defaultMessage: 'Low confidence ({count})',
              },
              { count: correctLow.length },
            )}
            checked={filters.correctLow}
            onChange={() => onFilterChange('correctLow')}
          />
        </fieldset>
        {/* Incorrect Questions */}
        <fieldset
          style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
          data-testid="quiz-question-filter-incorrect-group"
        >
          <legend
            style={{ color: '#B30000', fontSize: 16, marginBottom: 16 }}
            data-testid="quiz-question-filter-incorrect-title"
          >
            {intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.incorrect_title',
                defaultMessage: 'Incorrect {quizTypeCapitalized}',
              },
              { quizTypeCapitalized },
            )}
          </legend>
          <Checkbox
            data-testid={`filter-incorrectHigh`}
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.incorrect_high',
                defaultMessage: 'High confidence ({count})',
              },
              { count: incorrectHigh.length },
            )}
            checked={filters.incorrectHigh}
            onChange={() => onFilterChange('incorrectHigh')}
          />
          <Checkbox
            data-testid={`filter-incorrectMedium`}
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.incorrect_medium',
                defaultMessage: 'Medium confidence ({count})',
              },
              { count: incorrectMedium.length },
            )}
            checked={filters.incorrectMedium}
            onChange={() => onFilterChange('incorrectMedium')}
          />
          <Checkbox
            data-testid={`filter-incorrectLow`}
            label={intl.formatMessage(
              {
                id: 'quiz.question_filter_panel.incorrect_low',
                defaultMessage: 'Low confidence ({count})',
              },
              { count: incorrectLow.length },
            )}
            checked={filters.incorrectLow}
            onChange={() => onFilterChange('incorrectLow')}
          />
        </fieldset>
      </Box>
    </Box>
  );
};

export default QuizQuestionFilterPanel;
